import { useEffect } from "react";

/**
 * Watches `proseRef.current` for interactive widgets (and admin-tagged
 * [data-uvc-interactive] blocks) and pops an Aria hint via `ariaRef.current.say()`
 * when one enters the viewport. Tagged blocks fire near-instantly (250ms);
 * auto-detected widgets get a 3.5s grace window. Capped at 3 hints per mount
 * to keep the noise floor down.
 *
 * This is the same logic that lives inside `ReadingExperience.jsx` — exposed
 * as a hook so the editor's Preview tab and (future) public-share views can
 * reuse it without duplicating the IntersectionObserver bookkeeping.
 */
export function useInteractiveHints({ proseRef, ariaRef, enabled = true, deps = [] }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return undefined;
    const prose = proseRef.current;
    if (!prose) return undefined;

    const selector = [
      "button",
      "a[href]:not([href=''])",
      "details",
      "summary",
      "input:not([type='hidden'])",
      "select",
      "textarea",
      "iframe",
      "[role='button']",
      "[onclick]",
      "[data-uvc-interactive]",
    ].join(",");

    // (`hintFor` helper removed in iter 24 — we no longer show speech bubbles
    // for interactive sections; Aria just pulses a red dot on her avatar.)

    const interacted = new WeakSet();
    const hinted = new WeakSet();
    let hintsGiven = 0;
    const MAX_HINTS = 3;
    const pendingTimers = new Map();

    const armHint = (el) => {
      if (hinted.has(el) || interacted.has(el) || hintsGiven >= MAX_HINTS || pendingTimers.has(el)) return;
      const delay = el.hasAttribute?.("data-uvc-interactive") ? 250 : 3500;
      const t = setTimeout(() => {
        pendingTimers.delete(el);
        if (interacted.has(el) || hinted.has(el) || hintsGiven >= MAX_HINTS) return;
        hinted.add(el);
        hintsGiven += 1;
        // Per iter-24 user request: Aria stays in her home corner. No more
        // floating-out, no more page-jumps via scrollIntoView, no chat
        // bubble. We just light up a red dot + pulse on her avatar so the
        // learner knows there's an interactive section nearby.
        el.setAttribute("data-uvc-interactive-hint", "1");
        ariaRef.current?.nudge?.();
        setTimeout(() => { try { el.removeAttribute("data-uvc-interactive-hint"); } catch { /* ignore */ } }, 6000);
      }, delay);
      pendingTimers.set(el, t);
    };
    const cancelHint = (el) => {
      const t = pendingTimers.get(el);
      if (t) { clearTimeout(t); pendingTimers.delete(el); }
    };

    let io;
    let cancelled = false;
    const setupTimer = setTimeout(() => {
      if (cancelled) return;
      const nodes = Array.from(prose.querySelectorAll(selector));
      if (nodes.length === 0) return;
      nodes.forEach((n) => {
        const onInteract = () => {
          interacted.add(n);
          cancelHint(n);
          n.removeAttribute("data-uvc-interactive-hint");
        };
        n.addEventListener("mousedown", onInteract, { passive: true });
        n.addEventListener("touchstart", onInteract, { passive: true });
        n.addEventListener("focusin", onInteract, { passive: true });
      });
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.4) armHint(e.target);
          else cancelHint(e.target);
        }
      }, { threshold: [0, 0.4, 0.8] });
      nodes.forEach((n) => io.observe(n));
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(setupTimer);
      pendingTimers.forEach((t) => clearTimeout(t));
      pendingTimers.clear();
      if (io) io.disconnect();
    };
  }, [enabled, ...deps]);
}
